import React, { useState, useEffect } from 'react';
import { 
  Palette, Save, RotateCcw, Eye, Monitor, Smartphone,
  Sun, Moon, Loader2, Check, Type, Square, Circle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { presetThemes } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const defaultTheme = {
  primaryColor: '#E91E63',
  secondaryColor: '#1e3a5f',
  accentColor: '#10b981',
  backgroundColor: '#FDF8F4',
  cardBackground: '#ffffff',
  headerBackground: '#1e3a5f',
  footerBackground: '#1e3a5f',
  textColor: '#232323',
  textLight: '#6b7280',
  headerText: '#ffffff',
  footerText: '#ffffff',
  linkColor: '#E91E63',
  buttonPrimary: '#E91E63',
  buttonPrimaryText: '#ffffff',
  buttonSecondary: '#1e3a5f',
  buttonSecondaryText: '#ffffff',
  buttonDanger: '#ef4444',
  buttonDangerText: '#ffffff',
  inputBackground: '#ffffff',
  inputBorder: '#d1d5db',
  inputFocus: '#E91E63',
  fontFamily: 'Inter',
  headingFont: 'Poppins',
  borderRadius: 'medium',
  preset: 'custom'
};

const fontOptions = [
  { value: 'Inter', label: 'Inter (Modern)' },
  { value: 'Poppins', label: 'Poppins (Friendly)' },
  { value: 'Roboto', label: 'Roboto (Clean)' },
  { value: 'Open Sans', label: 'Open Sans (Readable)' },
  { value: 'Lato', label: 'Lato (Elegant)' },
  { value: 'Nunito', label: 'Nunito (Rounded)' },
  { value: 'Playfair Display', label: 'Playfair (Serif)' },
  { value: 'Montserrat', label: 'Montserrat (Bold)' }
];

const borderRadiusOptions = [
  { value: 'small', label: 'Small (4px)', icon: <Square className="w-4 h-4" /> },
  { value: 'medium', label: 'Medium (8px)', icon: <Square className="w-4 h-4 rounded" /> },
  { value: 'large', label: 'Large (16px)', icon: <Square className="w-4 h-4 rounded-lg" /> },
  { value: 'full', label: 'Pill (Full)', icon: <Circle className="w-4 h-4" /> }
];

const ColorPicker = ({ label, value, onChange, description }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium">{label}</Label>
    <div className="flex gap-2">
      <input
        type="color"
        value={value?.startsWith('#') ? value : '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-10 rounded cursor-pointer border-2 border-gray-200 hover:border-gray-300 transition-colors"
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        className="font-mono text-sm"
      />
    </div>
    {description && <p className="text-xs text-gray-500">{description}</p>}
  </div>
);

// Gradient Picker Component
const GradientPicker = ({ label, value, onChange, description }) => {
  // Parse existing gradient or use defaults
  const isGradient = value?.includes('gradient');
  const [useGradient, setUseGradient] = useState(isGradient);
  const [color1, setColor1] = useState(isGradient ? extractGradientColors(value)[0] : value || '#E91E63');
  const [color2, setColor2] = useState(isGradient ? extractGradientColors(value)[1] : '#9333ea');
  const [direction, setDirection] = useState(isGradient ? extractGradientDirection(value) : '135deg');
  
  useEffect(() => {
    if (useGradient) {
      onChange(`linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)`);
    } else {
      onChange(color1);
    }
  }, [useGradient, color1, color2, direction]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <button
          type="button"
          onClick={() => setUseGradient(!useGradient)}
          className={`px-2 py-1 text-xs rounded-full transition-colors ${
            useGradient 
              ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {useGradient ? 'Gradient ✓' : 'Solid Color'}
        </button>
      </div>
      
      {useGradient ? (
        <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
          {/* Gradient Preview */}
          <div 
            className="h-10 rounded-lg border border-gray-200"
            style={{ background: `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)` }}
          />
          
          {/* Color 1 */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16">Start:</span>
            <input
              type="color"
              value={color1}
              onChange={(e) => setColor1(e.target.value)}
              className="w-8 h-8 rounded cursor-pointer border border-gray-200"
            />
            <Input
              value={color1}
              onChange={(e) => setColor1(e.target.value)}
              className="font-mono text-xs h-8 flex-1"
            />
          </div>
          
          {/* Color 2 */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16">End:</span>
            <input
              type="color"
              value={color2}
              onChange={(e) => setColor2(e.target.value)}
              className="w-8 h-8 rounded cursor-pointer border border-gray-200"
            />
            <Input
              value={color2}
              onChange={(e) => setColor2(e.target.value)}
              className="font-mono text-xs h-8 flex-1"
            />
          </div>
          
          {/* Direction */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16">Angle:</span>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              className="flex-1 px-2 py-1 text-xs rounded border border-gray-200 bg-white"
            >
              <option value="0deg">↑ Top (0°)</option>
              <option value="45deg">↗ Top Right (45°)</option>
              <option value="90deg">→ Right (90°)</option>
              <option value="135deg">↘ Bottom Right (135°)</option>
              <option value="180deg">↓ Bottom (180°)</option>
              <option value="225deg">↙ Bottom Left (225°)</option>
              <option value="270deg">← Left (270°)</option>
              <option value="315deg">↖ Top Left (315°)</option>
            </select>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            type="color"
            value={color1}
            onChange={(e) => setColor1(e.target.value)}
            className="w-12 h-10 rounded cursor-pointer border-2 border-gray-200 hover:border-gray-300 transition-colors"
          />
          <Input
            value={color1}
            onChange={(e) => setColor1(e.target.value)}
            placeholder="#000000"
            className="font-mono text-sm"
          />
        </div>
      )}
      
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
  );
};

// Helper functions for gradient parsing
const extractGradientColors = (gradient) => {
  if (!gradient || !gradient.includes('gradient')) return ['#E91E63', '#9333ea'];
  const colorMatch = gradient.match(/#[a-fA-F0-9]{6}|#[a-fA-F0-9]{3}|rgb\([^)]+\)/g);
  return colorMatch ? [colorMatch[0] || '#E91E63', colorMatch[1] || '#9333ea'] : ['#E91E63', '#9333ea'];
};

const extractGradientDirection = (gradient) => {
  if (!gradient) return '135deg';
  const dirMatch = gradient.match(/(\d+)deg/);
  return dirMatch ? `${dirMatch[1]}deg` : '135deg';
};

const AdminThemeDesigner = () => {
  const [theme, setTheme] = useState(defaultTheme);
  const [originalTheme, setOriginalTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadTheme();
  }, []);

  useEffect(() => {
    // Apply theme preview to document
    applyPreview(theme);
    // Check for changes
    setHasChanges(JSON.stringify(theme) !== JSON.stringify(originalTheme));
  }, [theme, originalTheme]);

  const applyPreview = (t) => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', t.primaryColor);
    root.style.setProperty('--color-secondary', t.secondaryColor);
    root.style.setProperty('--color-accent', t.accentColor);
    root.style.setProperty('--color-background', t.backgroundColor);
    root.style.setProperty('--color-card-bg', t.cardBackground);
    root.style.setProperty('--color-header-bg', t.headerBackground);
    root.style.setProperty('--color-footer-bg', t.footerBackground);
    root.style.setProperty('--color-text', t.textColor);
    root.style.setProperty('--color-text-light', t.textLight);
    root.style.setProperty('--color-header-text', t.headerText);
    root.style.setProperty('--color-footer-text', t.footerText);
    root.style.setProperty('--color-link', t.linkColor);
    root.style.setProperty('--color-btn-primary', t.buttonPrimary);
    root.style.setProperty('--color-btn-primary-text', t.buttonPrimaryText);
    root.style.setProperty('--color-btn-secondary', t.buttonSecondary);
    root.style.setProperty('--color-btn-secondary-text', t.buttonSecondaryText);
  };

  const loadTheme = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/theme-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const loadedTheme = { ...defaultTheme, ...data };
        setTheme(loadedTheme);
        setOriginalTheme(loadedTheme);
      }
    } catch (error) {
      console.error('Failed to load theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/theme-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(theme)
      });
      
      if (response.ok) {
        setOriginalTheme(theme);
        toast({
          title: "Theme Saved",
          description: "Your theme has been applied site-wide",
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save theme settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTheme(originalTheme);
    applyPreview(originalTheme);
    toast({
      title: "Reset",
      description: "Changes have been discarded",
    });
  };

  const handleResetToDefault = async () => {
    // Reset to original qbool default theme
    setTheme({ ...defaultTheme, preset: 'light' });
    applyPreview(defaultTheme);
    
    // Save to database
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/theme-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...defaultTheme, preset: 'light' })
      });
      
      if (response.ok) {
        setOriginalTheme({ ...defaultTheme, preset: 'light' });
        toast({
          title: "✓ Reset to Default",
          description: "Original qbool theme has been restored",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reset theme",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePresetSelect = (presetName) => {
    const preset = presetThemes[presetName];
    if (preset) {
      setTheme({ ...preset, preset: presetName });
      toast({
        title: "Preset Applied",
        description: `${presetName.charAt(0).toUpperCase() + presetName.slice(1)} theme loaded. Save to apply.`,
      });
    }
  };

  const updateTheme = (key, value) => {
    setTheme(prev => ({ ...prev, [key]: value, preset: 'custom' }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Theme Designer</h1>
          <p className="text-gray-600">Customize your website&apos;s colors, fonts, and style</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={handleResetToDefault}
            disabled={saving}
            className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Original
          </Button>
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Discard Changes
            </Button>
          )}
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasChanges}
            className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Theme</>
            )}
          </Button>
        </div>
      </div>

      {/* Preset Themes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Palette className="w-5 h-5" />
            Preset Themes
            <span className="text-xs font-normal text-gray-500 ml-2">({Object.keys(presetThemes).length} themes)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-3">
            {Object.entries(presetThemes).map(([name, preset]) => (
              <button
                key={name}
                onClick={() => handlePresetSelect(name)}
                className={`relative p-3 rounded-xl border-2 transition-all hover:scale-105 ${
                  theme.preset === name 
                    ? 'border-pink-500 ring-2 ring-pink-200' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                title={name.charAt(0).toUpperCase() + name.slice(1)}
              >
                <div className="flex gap-0.5 mb-2 justify-center">
                  <div 
                    className="w-5 h-5 rounded-full border-2 border-white shadow-sm" 
                    style={{ backgroundColor: preset.primaryColor }} 
                  />
                  <div 
                    className="w-5 h-5 rounded-full border-2 border-white shadow-sm -ml-2" 
                    style={{ backgroundColor: preset.secondaryColor }} 
                  />
                  <div 
                    className="w-5 h-5 rounded-full border-2 border-white shadow-sm -ml-2" 
                    style={{ backgroundColor: preset.accentColor }} 
                  />
                </div>
                <span className="text-xs font-medium capitalize truncate w-full">{name}</span>
                {theme.preset === name && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-pink-500 rounded-full flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Main Editor */}
      <Tabs defaultValue="colors" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="colors">Colors</TabsTrigger>
          <TabsTrigger value="buttons">Buttons</TabsTrigger>
          <TabsTrigger value="typography">Typography</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        {/* Colors Tab */}
        <TabsContent value="colors" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Brand Colors */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Brand Colors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ColorPicker
                  label="Primary Color"
                  value={theme.primaryColor}
                  onChange={(v) => updateTheme('primaryColor', v)}
                  description="Main brand color - buttons, links, accents"
                />
                <ColorPicker
                  label="Secondary Color"
                  value={theme.secondaryColor}
                  onChange={(v) => updateTheme('secondaryColor', v)}
                  description="Supporting color - headers, backgrounds"
                />
                <ColorPicker
                  label="Accent Color"
                  value={theme.accentColor}
                  onChange={(v) => updateTheme('accentColor', v)}
                  description="Highlights and special elements"
                />
              </CardContent>
            </Card>

            {/* Background Colors */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Backgrounds</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <GradientPicker
                  label="Page Background"
                  value={theme.backgroundColor}
                  onChange={(v) => updateTheme('backgroundColor', v)}
                  description="Main page background color or gradient"
                />
                <ColorPicker
                  label="Card Background"
                  value={theme.cardBackground}
                  onChange={(v) => updateTheme('cardBackground', v)}
                  description="Cards and content boxes"
                />
                <GradientPicker
                  label="Header Background"
                  value={theme.headerBackground}
                  onChange={(v) => updateTheme('headerBackground', v)}
                  description="Navigation header - supports gradient"
                />
                <GradientPicker
                  label="Footer Background"
                  value={theme.footerBackground}
                  onChange={(v) => updateTheme('footerBackground', v)}
                  description="Page footer - supports gradient"
                />
              </CardContent>
            </Card>

            {/* Text Colors */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Text Colors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ColorPicker
                  label="Main Text"
                  value={theme.textColor}
                  onChange={(v) => updateTheme('textColor', v)}
                  description="Primary text color"
                />
                <ColorPicker
                  label="Light Text"
                  value={theme.textLight}
                  onChange={(v) => updateTheme('textLight', v)}
                  description="Secondary/muted text"
                />
                <ColorPicker
                  label="Link Color"
                  value={theme.linkColor}
                  onChange={(v) => updateTheme('linkColor', v)}
                  description="Hyperlinks"
                />
              </CardContent>
            </Card>

            {/* Header/Footer Text */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Header & Footer Text</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ColorPicker
                  label="Header Text"
                  value={theme.headerText}
                  onChange={(v) => updateTheme('headerText', v)}
                  description="Navigation menu text"
                />
                <ColorPicker
                  label="Footer Text"
                  value={theme.footerText}
                  onChange={(v) => updateTheme('footerText', v)}
                  description="Footer links and text"
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Buttons Tab */}
        <TabsContent value="buttons" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Primary Button */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Primary Button</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <GradientPicker
                  label="Background"
                  value={theme.buttonPrimary}
                  onChange={(v) => updateTheme('buttonPrimary', v)}
                  description="Supports gradient colors"
                />
                <ColorPicker
                  label="Text"
                  value={theme.buttonPrimaryText}
                  onChange={(v) => updateTheme('buttonPrimaryText', v)}
                />
                <div className="pt-2">
                  <button
                    className="w-full py-2 px-4 rounded-lg font-medium transition-transform hover:scale-105"
                    style={{ 
                      background: theme.buttonPrimary, 
                      color: theme.buttonPrimaryText,
                      borderRadius: theme.borderRadius === 'full' ? '9999px' : theme.borderRadius === 'large' ? '16px' : theme.borderRadius === 'small' ? '4px' : '8px'
                    }}
                  >
                    Primary Button
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Secondary Button */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Secondary Button</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <GradientPicker
                  label="Background"
                  value={theme.buttonSecondary}
                  onChange={(v) => updateTheme('buttonSecondary', v)}
                  description="Supports gradient colors"
                />
                <ColorPicker
                  label="Text"
                  value={theme.buttonSecondaryText}
                  onChange={(v) => updateTheme('buttonSecondaryText', v)}
                />
                <div className="pt-2">
                  <button
                    className="w-full py-2 px-4 rounded-lg font-medium transition-transform hover:scale-105"
                    style={{ 
                      background: theme.buttonSecondary, 
                      color: theme.buttonSecondaryText,
                      borderRadius: theme.borderRadius === 'full' ? '9999px' : theme.borderRadius === 'large' ? '16px' : theme.borderRadius === 'small' ? '4px' : '8px'
                    }}
                  >
                    Secondary Button
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Danger Button */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Danger Button</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <GradientPicker
                  label="Background"
                  value={theme.buttonDanger}
                  onChange={(v) => updateTheme('buttonDanger', v)}
                  description="Supports gradient colors"
                />
                <ColorPicker
                  label="Text"
                  value={theme.buttonDangerText}
                  onChange={(v) => updateTheme('buttonDangerText', v)}
                />
                <div className="pt-2">
                  <button
                    className="w-full py-2 px-4 rounded-lg font-medium transition-transform hover:scale-105"
                    style={{ 
                      background: theme.buttonDanger, 
                      color: theme.buttonDangerText,
                      borderRadius: theme.borderRadius === 'full' ? '9999px' : theme.borderRadius === 'large' ? '16px' : theme.borderRadius === 'small' ? '4px' : '8px'
                    }}
                  >
                    Danger Button
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Input Colors */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Form Inputs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ColorPicker
                  label="Input Background"
                  value={theme.inputBackground}
                  onChange={(v) => updateTheme('inputBackground', v)}
                />
                <ColorPicker
                  label="Input Border"
                  value={theme.inputBorder}
                  onChange={(v) => updateTheme('inputBorder', v)}
                />
                <ColorPicker
                  label="Focus Ring"
                  value={theme.inputFocus}
                  onChange={(v) => updateTheme('inputFocus', v)}
                />
              </div>
              <div className="mt-4">
                <input
                  type="text"
                  placeholder="Sample input field"
                  className="w-full max-w-md px-4 py-2 rounded-lg border-2 outline-none transition-all focus:ring-2"
                  style={{
                    backgroundColor: theme.inputBackground,
                    borderColor: theme.inputBorder,
                    color: theme.textColor,
                    '--tw-ring-color': theme.inputFocus,
                    borderRadius: theme.borderRadius === 'full' ? '9999px' : theme.borderRadius === 'large' ? '16px' : theme.borderRadius === 'small' ? '4px' : '8px'
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Typography Tab */}
        <TabsContent value="typography" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Font Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Type className="w-4 h-4" />
                  Fonts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Body Font</Label>
                  <select
                    value={theme.fontFamily}
                    onChange={(e) => updateTheme('fontFamily', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white"
                  >
                    {fontOptions.map(font => (
                      <option key={font.value} value={font.value}>{font.label}</option>
                    ))}
                  </select>
                  <p className="text-sm" style={{ fontFamily: theme.fontFamily }}>
                    The quick brown fox jumps over the lazy dog.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Heading Font</Label>
                  <select
                    value={theme.headingFont}
                    onChange={(e) => updateTheme('headingFont', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white"
                  >
                    {fontOptions.map(font => (
                      <option key={font.value} value={font.value}>{font.label}</option>
                    ))}
                  </select>
                  <h3 className="text-xl font-bold" style={{ fontFamily: theme.headingFont }}>
                    Heading Example
                  </h3>
                </div>
              </CardContent>
            </Card>

            {/* Border Radius */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Border Radius</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {borderRadiusOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateTheme('borderRadius', option.value)}
                      className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                        theme.borderRadius === option.value
                          ? 'border-pink-500 bg-pink-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {option.icon}
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Eye className="w-4 h-4" />
                  Live Preview
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant={previewMode === 'desktop' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('desktop')}
                  >
                    <Monitor className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={previewMode === 'mobile' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('mobile')}
                  >
                    <Smartphone className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div 
                className={`mx-auto border-4 border-gray-300 rounded-xl overflow-hidden shadow-lg transition-all ${
                  previewMode === 'mobile' ? 'max-w-[375px]' : 'max-w-full'
                }`}
              >
                {/* Preview Header */}
                <div 
                  className="px-4 py-3 flex justify-between items-center"
                  style={{ background: theme.headerBackground }}
                >
                  <span className="font-bold" style={{ color: theme.headerText, fontFamily: theme.headingFont }}>
                    qbool.ca
                  </span>
                  <div className="flex gap-3" style={{ color: theme.headerText }}>
                    <span className="text-sm">Home</span>
                    <span className="text-sm">Search</span>
                    <span className="text-sm">Login</span>
                  </div>
                </div>

                {/* Preview Content */}
                <div className="p-6" style={{ background: theme.backgroundColor }}>
                  <h1 
                    className="text-2xl font-bold mb-2"
                    style={{ color: theme.textColor, fontFamily: theme.headingFont }}
                  >
                    Find Your Perfect Match
                  </h1>
                  <p 
                    className="mb-4"
                    style={{ color: theme.textLight, fontFamily: theme.fontFamily }}
                  >
                    A trusted Islamic matrimonial service connecting Muslims worldwide.
                  </p>

                  {/* Sample Card */}
                  <div 
                    className="p-4 rounded-lg mb-4 shadow-sm"
                    style={{ 
                      backgroundColor: theme.cardBackground,
                      borderRadius: theme.borderRadius === 'full' ? '24px' : theme.borderRadius === 'large' ? '16px' : theme.borderRadius === 'small' ? '4px' : '8px'
                    }}
                  >
                    <h3 
                      className="font-semibold mb-2"
                      style={{ color: theme.textColor, fontFamily: theme.headingFont }}
                    >
                      Featured Profile
                    </h3>
                    <p 
                      className="text-sm mb-3"
                      style={{ color: theme.textLight, fontFamily: theme.fontFamily }}
                    >
                      28 years • Software Engineer • Toronto, Canada
                    </p>
                    <a 
                      href="#" 
                      className="text-sm font-medium"
                      style={{ color: theme.linkColor }}
                      onClick={(e) => e.preventDefault()}
                    >
                      View Profile →
                    </a>
                  </div>

                  {/* Sample Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <button
                      className="px-4 py-2 font-medium transition-transform hover:scale-105"
                      style={{ 
                        background: theme.buttonPrimary, 
                        color: theme.buttonPrimaryText,
                        borderRadius: theme.borderRadius === 'full' ? '9999px' : theme.borderRadius === 'large' ? '16px' : theme.borderRadius === 'small' ? '4px' : '8px'
                      }}
                    >
                      Get Started
                    </button>
                    <button
                      className="px-4 py-2 font-medium transition-transform hover:scale-105"
                      style={{ 
                        background: theme.buttonSecondary, 
                        color: theme.buttonSecondaryText,
                        borderRadius: theme.borderRadius === 'full' ? '9999px' : theme.borderRadius === 'large' ? '16px' : theme.borderRadius === 'small' ? '4px' : '8px'
                      }}
                    >
                      Learn More
                    </button>
                  </div>
                </div>

                {/* Preview Footer */}
                <div 
                  className="px-4 py-3 text-center text-sm"
                  style={{ background: theme.footerBackground, color: theme.footerText }}
                >
                  © 2026 qbool.ca • Privacy Policy • Terms
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Floating Save Button (when changes exist) */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            size="lg"
            className="shadow-lg bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
          >
            {saving ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-5 h-5 mr-2" /> Save Changes</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminThemeDesigner;
